import type { Vec2 } from '@4bitlabs/vec2';
import {
  convolution1D as conv1d,
  K_GAUSS_BLUR_3,
} from '@watercolorizer/convolution';
import { assertOK } from './assert-vec2-ok.js';
import {
  type DistortSegmentOptions,
  distortSegment,
} from './distort-segment.js';
import { segments } from './polygons-helpers.js';
import { type GaussianRngFn, unsafeGaussRng } from './rng.js';
import type { PointsAndWeights } from './types.js';
import { wigglePolygon } from './wiggle-polygon.js';
import { zipper } from './zipper.js';

export interface DistortPolyOptions extends DistortSegmentOptions {
  blurWeightsOnDistort?: boolean;
  gaussRng?: GaussianRngFn;
  wiggle?: boolean;
}

function distortPoly(
  type: 'loop' | 'line',
  [points, weights]: PointsAndWeights,
  options: DistortPolyOptions = {},
): PointsAndWeights {
  const {
    blurWeightsOnDistort = false,
    gaussRng = unsafeGaussRng,
    wiggle = true,
  } = options;

  const midPoints: Vec2[] = [];
  const midWeights: number[] = [];

  for (const segment of segments(points, type)) {
    const [a, b, i0, i1] = segment;
    const w0 = weights[i0] ?? 1;
    const w1 = weights[i1] ?? 1;

    const [midPoint, weight] = distortSegment(a, b, w0, w1, options);
    midPoints.push(midPoint);
    midWeights.push(weight);
  }

  const prevPoints = wiggle
    ? wigglePolygon(points, weights, { gaussRng })
    : points;

  const nextPoints = Array.from(zipper(prevPoints, midPoints));

  let nextWeights = Array.from(zipper(weights, midWeights));
  if (blurWeightsOnDistort)
    nextWeights = conv1d(
      K_GAUSS_BLUR_3,
      nextWeights,
      Array.from<number>({ length: nextWeights.length }),
    );

  return [assertOK(nextPoints), nextWeights];
}

export const distortPolygon = (
  pnw: PointsAndWeights,
  options: DistortPolyOptions = {},
) => distortPoly('loop', pnw, options);

export const distortPolyline = (
  pnw: PointsAndWeights,
  options: DistortPolyOptions = {},
) => distortPoly('line', pnw, options);
